import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';
import { TabViewModule } from 'primeng/tabview';
import { ToastModule } from 'primeng/toast';
import { ChartModule } from 'primeng/chart';
import { CardModule } from 'primeng/card';
import { MessageService } from 'primeng/api';
import { PostComponent } from '../../components/post/post.component';
import { JobCardComponent } from '../../components/job-card/job-card.component';
import { OrganizationDto } from '../../dto/organization.dto';
import { OrganizationService } from '../../services/organization.service';
import {
  FeedPost,
  MockFeedService,
} from '../../services-mock/mock-feed.service';
import { Job, MockJobService } from '../../services-mock/mock-job.service';
import { MockOrganizationService } from '../../services-mock/mock-organization.service';

@Component({
  selector: 'app-organization',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ButtonModule,
    AvatarModule,
    TabViewModule,
    ToastModule,
    ChartModule,
    CardModule,
    PostComponent,
    JobCardComponent,
  ],
  providers: [MessageService],
  template: `
    <div class="organization-container">
      <div class="organization-header">
        <div class="organization-avatar-section">
          <p-avatar
            [image]="organization?.avatar"
            [label]="organization?.initials || 'O'"
            shape="circle"
            size="xlarge"
            styleClass="organization-avatar"
          ></p-avatar>
          <div class="organization-actions">
            <button
              pButton
              type="button"
              [label]="organization?.followersCount + ' Followers'"
              class="follow-button"
              (click)="navigateToFollowers()"
            ></button>
            <button
              pButton
              type="button"
              [label]="organization?.followingCount + ' Following'"
              class="follow-button"
              (click)="navigateToFollowing()"
            ></button>
          </div>
        </div>
        <div class="organization-info">
          <h1 class="organization-name">{{ organization?.name }}</h1>
          <div class="organization-location" *ngIf="organization?.location">
            <i class="pi pi-map-marker"></i>
            {{ organization?.location }}
          </div>
          <div
            class="organization-description"
            *ngIf="organization?.description"
          >
            {{ organization?.description }}
          </div>
          <div
            class="organization-website"
            *ngIf="organization && organization.website"
          >
            <a
              [href]="organization.website"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i class="pi pi-globe"></i>
              {{ organization.website }}
            </a>
          </div>
        </div>
      </div>

      <div class="organization-content">
        <p-tabView>
          <p-tabPanel header="Posts">
            <div class="posts-section">
              <div class="posts-count" *ngIf="organizationPosts.length > 0">
                {{ organizationPosts.length }} post{{
                  organizationPosts.length !== 1 ? 's' : ''
                }}
              </div>
              <div class="posts-list" *ngIf="organizationPosts.length > 0">
                <app-post
                  *ngFor="let post of organizationPosts"
                  [post]="post"
                ></app-post>
              </div>
              <div
                class="no-posts"
                *ngIf="organizationPosts.length === 0 && !loadingPosts"
              >
                <p>No posts yet.</p>
              </div>
              <div class="loading-posts" *ngIf="loadingPosts">
                <p>Loading posts...</p>
              </div>
            </div>
          </p-tabPanel>

          <p-tabPanel header="Jobs">
            <div class="jobs-section">
              <div class="jobs-count" *ngIf="organizationJobs.length > 0">
                {{ organizationJobs.length }} job{{
                  organizationJobs.length !== 1 ? 's' : ''
                }}
              </div>
              <div class="jobs-grid" *ngIf="organizationJobs.length > 0">
                <app-job-card
                  *ngFor="let job of organizationJobs"
                  [job]="job"
                ></app-job-card>
              </div>
              <div
                class="no-jobs"
                *ngIf="organizationJobs.length === 0 && !loadingJobs"
              >
                <p>No jobs posted yet.</p>
              </div>
              <div class="loading-jobs" *ngIf="loadingJobs">
                <p>Loading jobs...</p>
              </div>
            </div>
          </p-tabPanel>

          <p-tabPanel header="Metrics">
            <div class="metrics-section">
              <div class="metrics-grid">
                <!-- Connections by Month -->
                <p-card title="Connections by Month" styleClass="metric-card">
                  <p-chart
                    type="line"
                    [data]="connectionsChartData"
                    [options]="chartOptions"
                    [height]="'300px'"
                  ></p-chart>
                </p-card>

                <!-- Plays by Month -->
                <p-card title="Plays by Month" styleClass="metric-card">
                  <p-chart
                    type="bar"
                    [data]="playsChartData"
                    [options]="chartOptions"
                    [height]="'300px'"
                  ></p-chart>
                </p-card>

                <!-- Applies by Month -->
                <p-card title="Applies by Month" styleClass="metric-card">
                  <p-chart
                    type="bar"
                    [data]="appliesChartData"
                    [options]="chartOptions"
                    [height]="'300px'"
                  ></p-chart>
                </p-card>

                <!-- Posts by Category -->
                <p-card title="Posts by Category" styleClass="metric-card">
                  <p-chart
                    type="doughnut"
                    [data]="postsByCategoryChartData"
                    [options]="doughnutChartOptions"
                    [height]="'300px'"
                  ></p-chart>
                </p-card>
              </div>
            </div>
          </p-tabPanel>
        </p-tabView>
      </div>
    </div>
    <p-toast></p-toast>
  `,
  styles: [
    `
      .organization-container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 1.5rem;
        background-color: #f5f5f5;
        min-height: calc(100vh - 60px);
      }

      .organization-header {
        background-color: #ffffff;
        border-radius: 8px;
        padding: 2rem;
        margin-bottom: 1.5rem;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }

      .organization-avatar-section {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-bottom: 1.5rem;
      }

      .organization-actions {
        display: flex;
        gap: 1rem;
        margin-top: 1rem;
      }

      .follow-button {
        background: #0066cc;
        color: #ffffff;
        border: none;
      }

      .organization-info {
        text-align: center;
      }

      .organization-name {
        font-size: 2rem;
        font-weight: 600;
        color: #000000;
        margin: 0 0 0.5rem 0;
      }

      .organization-location {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        color: #999999;
        font-size: 0.95rem;
        margin-bottom: 1rem;
      }

      .organization-description {
        color: #333333;
        line-height: 1.6;
        max-width: 600px;
        margin: 0 auto 1rem auto;
      }

      .organization-website {
        margin-top: 1rem;
      }

      .organization-website a {
        color: #0066cc;
        text-decoration: none;
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.95rem;
      }

      .organization-website a:hover {
        text-decoration: underline;
      }

      .organization-content {
        background-color: #ffffff;
        border-radius: 8px;
        padding: 1.5rem;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }

      .posts-section,
      .jobs-section {
        padding: 1rem 0;
      }

      .posts-count,
      .jobs-count {
        font-size: 1rem;
        color: #666666;
        margin-bottom: 1rem;
      }

      .posts-list {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }

      .jobs-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
        gap: 1.5rem;
      }

      .no-posts,
      .no-jobs {
        text-align: center;
        padding: 3rem;
        color: #666666;
      }

      .loading-posts,
      .loading-jobs {
        text-align: center;
        padding: 2rem;
        color: #666666;
      }

      .metrics-section {
        padding: 1rem 0;
      }

      .metrics-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
        gap: 1.5rem;
      }

      .metric-card {
        height: 100%;
      }

      @media (max-width: 768px) {
        .metrics-grid {
          grid-template-columns: 1fr;
        }
      }

      :host ::ng-deep {
        .organization-avatar {
          width: 120px;
          height: 120px;
          font-size: 3rem;
        }

        .p-tabview-nav {
          background: transparent;
        }

        .p-tabview-nav-link {
          padding: 1rem 1.5rem;
        }
      }
    `,
  ],
})
export class OrganizationComponent implements OnInit {
  organization: OrganizationDto | null = null;
  organizationPosts: FeedPost[] = [];
  organizationJobs: Job[] = [];
  loadingPosts = false;
  loadingJobs = false;
  organizationId: string | null = null;

  // Chart data
  connectionsChartData: any = {
    labels: [],
    datasets: [],
  };
  playsChartData: any = {
    labels: [],
    datasets: [],
  };
  appliesChartData: any = {
    labels: [],
    datasets: [],
  };
  postsByCategoryChartData: any = {
    labels: [],
    datasets: [],
  };

  chartOptions: any;
  doughnutChartOptions: any;

  constructor(
    private organizationService: OrganizationService,
    private mockOrganizationService: MockOrganizationService,
    private feedService: MockFeedService,
    private jobService: MockJobService,
    private router: Router,
    private route: ActivatedRoute,
    private messageService: MessageService
  ) {
    this.setupChartOptions();
  }

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      this.organizationId = params.get('id');
      if (this.organizationId) {
        this.loadOrganization();
        this.loadOrganizationPosts();
        this.loadOrganizationJobs();
        this.loadMetrics();
      }
    });
  }

  setupChartOptions() {
    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          labels: {
            color: '#495057',
          },
        },
      },
      scales: {
        x: {
          ticks: {
            color: '#495057',
          },
          grid: {
            color: '#ebedef',
          },
        },
        y: {
          ticks: {
            color: '#495057',
          },
          grid: {
            color: '#ebedef',
          },
        },
      },
    };

    this.doughnutChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'right',
          labels: {
            color: '#495057',
          },
        },
      },
    };
  }

  loadMetrics() {
    if (!this.organizationId) return;

    this.mockOrganizationService
      .getOrganizationMetrics(this.organizationId)
      .subscribe({
        next: (metrics) => {
          // Connections by Month
          this.connectionsChartData = {
            labels: metrics.connectionsByMonth.map((m) => m.month),
            datasets: [
              {
                label: 'Connections',
                data: metrics.connectionsByMonth.map((m) => m.count),
                fill: false,
                borderColor: '#0066cc',
                tension: 0.4,
              },
            ],
          };

          // Plays by Month
          this.playsChartData = {
            labels: metrics.playsByMonth.map((m) => m.month),
            datasets: [
              {
                label: 'Plays',
                data: metrics.playsByMonth.map((m) => m.count),
                backgroundColor: '#42A5F5',
              },
            ],
          };

          // Applies by Month
          this.appliesChartData = {
            labels: metrics.appliesByMonth.map((m) => m.month),
            datasets: [
              {
                label: 'Applies',
                data: metrics.appliesByMonth.map((m) => m.count),
                backgroundColor: '#66BB6A',
              },
            ],
          };

          // Posts by Category
          this.postsByCategoryChartData = {
            labels: metrics.postsByCategory.map((c) => c.category),
            datasets: [
              {
                data: metrics.postsByCategory.map((c) => c.count),
                backgroundColor: [
                  '#FF6384',
                  '#36A2EB',
                  '#FFCE56',
                  '#4BC0C0',
                  '#9966FF',
                  '#FF9F40',
                ],
              },
            ],
          };
        },
        error: (err) => {
          console.error('Failed to load metrics', err);
        },
      });
  }

  loadOrganization() {
    if (!this.organizationId) return;

    this.organizationService.getOrganization(this.organizationId).subscribe({
      next: (org) => {
        this.organization = org;
      },
      error: (err) => {
        console.error('Failed to load organization', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load organization',
        });
      },
    });
  }

  loadOrganizationPosts() {
    if (!this.organizationId) return;

    this.loadingPosts = true;
    this.feedService.getPosts().subscribe({
      next: (posts) => {
        // Filter posts by organizationId
        this.organizationPosts = posts.filter(
          (post) => post.author.organizationId === this.organizationId
        );
        this.loadingPosts = false;
      },
      error: (err) => {
        console.error('Failed to load posts', err);
        this.loadingPosts = false;
      },
    });
  }

  loadOrganizationJobs() {
    if (!this.organizationId) return;

    this.loadingJobs = true;
    this.jobService.getAllJobs().subscribe({
      next: (jobs) => {
        // Filter jobs by organization name
        if (this.organization) {
          this.organizationJobs = jobs.filter(
            (job) => job.company === this.organization!.name
          );
        }
        this.loadingJobs = false;
      },
      error: (err) => {
        console.error('Failed to load jobs', err);
        this.loadingJobs = false;
      },
    });
  }

  navigateToFollowers() {
    if (this.organizationId) {
      this.router.navigate(['/organization', this.organizationId, 'followers']);
    }
  }

  navigateToFollowing() {
    if (this.organizationId) {
      this.router.navigate(['/organization', this.organizationId, 'following']);
    }
  }
}
